import type { PMIssue } from "@/domains/pm/types";

/**
 * An issue's two scheduling dates, and the rules shared by every surface that
 * shows them. Kept apart from `dueDate.ts`, which is about *overdue* — a
 * due-date-and-status question that start dates play no part in.
 */

/**
 * True when a start date sits after its due date.
 *
 * This is a warned-about state, never a blocked one, and the database
 * deliberately permits it (pipeline migration 20260911101416). Every editing
 * surface except the Gantt writes one date at a time, so moving a window
 * forward has to pass through an inverted pair whichever field is edited
 * first; blocking would make the order of two independent edits load-bearing.
 * The bulk (multi-select) editors could not enforce it at all — there is no
 * single counterpart value to check against.
 *
 * So the pair is accepted, flagged where both values are visible together, and
 * made unreachable by construction in the one place that writes both at once:
 * the Gantt's drag geometry.
 */
export function isDateRangeInverted(issue: Pick<PMIssue, "start_date" | "due_date">): boolean {
  const { start_date: start, due_date: due } = issue;
  if (!start || !due) return false;
  // Both are date-only `yyyy-MM-dd` strings, which compare correctly as text —
  // no parsing, and therefore no timezone to get wrong.
  return start > due;
}

/**
 * Where an issue sits on a timeline, or null when it cannot be placed.
 *
 * An issue with only one date is still schedulable — it occupies that single
 * day — which is what lets the Gantt render a one-sided bar the user can drag
 * an edge off to set the other end. Only an issue with neither date is
 * unscheduled.
 */
export function issueDateRange(
  issue: Pick<PMIssue, "start_date" | "due_date">
): { start: string; end: string } | null {
  const { start_date: start, due_date: due } = issue;
  // Narrow before falling back, so neither end needs a non-null assertion:
  // past this point at least one of the two is a string.
  if (start && due) {
    // An inverted pair still has to draw somewhere; order the ends so callers
    // never receive a negative span.
    return start <= due ? { start, end: due } : { start: due, end: start };
  }
  if (start) return { start, end: start };
  if (due) return { start: due, end: due };
  return null;
}

/** True when the issue has neither date and so belongs in the unscheduled band. */
export function isIssueUnscheduled(issue: Pick<PMIssue, "start_date" | "due_date">): boolean {
  return !issue.start_date && !issue.due_date;
}

/**
 * The date an issue sorts by on a timeline: where its bar begins. Falls back to
 * the due date for an issue that only has one, and null-sorts last.
 */
export function effectiveStartDate(issue: Pick<PMIssue, "start_date" | "due_date">): string | null {
  return issue.start_date ?? issue.due_date ?? null;
}
